import React from "react";
import countries from "world-countries";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "../lib/supabaseClient";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { LockKeyhole } from "lucide-react";

import { PhoneInput } from "@/components/ui/phone-input";

// shadcn/ui
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import CustomCheckbox from "@/components/ui/checkbox_custom";

const heroImg = "/images/antoia_launch_event_2.png";

const RESIDENCE_COUNTRIES = countries
    .map((c) => ({
        iso2: c.cca2,
        name: c.name?.common ?? c.name?.official ?? c.cca2,
    }))
    .filter((c) => Boolean(c.iso2))
    .sort((a, b) => a.name.localeCompare(b.name));

function normalizeCheckboxValue(v) {
    if (typeof v === "boolean") return v;
    if (v?.target && typeof v.target.checked === "boolean") return v.target.checked;
    return false;
}

const schema = z
    .object({
        firstName: z.string().trim().min(1, "Required"),
        lastName: z.string().trim().min(1, "Required"),
        email: z.string().trim().email("Enter a valid email"),

        phone_e164: z
            .string()
            .min(1, "Required")
            .refine((v) => {
                const p = parsePhoneNumberFromString(v);
                return Boolean(p?.isValid());
            }, "Enter a valid phone number"),

        country: z.string().min(1, "Select a country"),

        consent: z.literal(true, {
            errorMap: () => ({ message: "Please accept the privacy terms" }),
        }),
        marketingOptIn: z.boolean().default(true),
    })
    .refine(
        (vals) => RESIDENCE_COUNTRIES.some((c) => c.iso2 === vals.country),
        { path: ["country"], message: "Select a valid country" }
    );

export default function LaunchEvent() {
    const registerRef = React.useRef(null);

    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone_e164: "",
            country: "",
            consent: false,
            marketingOptIn: false,
        },
        mode: "onBlur",
    });

    const countryOptions = countries
        .map((country) => ({
            label: country.name.common,
            value: country.cca2,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const [submitting, setSubmitting] = React.useState(false);
    const [success, setSuccess] = React.useState(false);
    const [error, setError] = React.useState("");

    function scrollToForm() {
        registerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    async function sendConfirmationEmail({ email, first_name, my_message }) {
        const response = await fetch("/api/eventPreRegConfirmation", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, first_name, my_message }),
        });

        // If the endpoint doesn't exist in dev, you'll see 404 here
        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`Email API failed: ${response.status} ${text}`);
        }

        const result = await response.json().catch(() => ({}));
        if (result?.error) throw new Error(result.error);

        return result;
    }

    const upsertKlaviyoApplicant = async (payload) => {
        const r = await fetch("/api/eventPreRegKlaviyo", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data?.error || "Klaviyo API failed");
        return data;
    };

    async function ensureAnonSession() {
        const { data } = await supabase.auth.getUser();
        if (data?.user) return data.user;

        const { data: signed, error } = await supabase.auth.signInAnonymously();
        if (error) throw error;
        return signed.user;
    }

    async function onSubmit(values) {
        setSubmitting(true);
        setError("");
        setSuccess(false);

        try {

            const parsed = parsePhoneNumberFromString(values.phone_e164);
            if (!parsed || !parsed.isValid()) throw new Error("Invalid phone");

            const phone_country_iso2 = parsed.country || null;
            const phone_calling_code = `+${parsed.countryCallingCode}`;
            const phone_national = parsed.nationalNumber;
            const phone_e164 = parsed.number;

            const myMessage = `
            Thank you for your interest in the Palais d’Antoia — Launch event.

            Your details have been received. This evening is intimate by design, with invitations handpicked to preserve the atmosphere of the night. If you are a member already or have requested access to the platform, this may positively influence the priority of your placement.
            
            If selected, you’ll receive a personal invitation with the date, location, and confirmation steps.
      `.trim();

            const selected = RESIDENCE_COUNTRIES.find((c) => c.iso2 === values.country);
            const residence_country_iso2 = values.country;           // ISO2
            const country_name = selected?.name ?? values.country; // fallback to iso2

            const EVENT_SLUG = "launch";

            const authUser = await ensureAnonSession();
            const emailNorm = values.email.trim().toLowerCase();

            // load product version id
            const { data: ev, error: evErr } = await supabase
                .from("events")
                .select("id")
                .eq("slug", EVENT_SLUG)
                .single();

            if (evErr) { console.error(evErr); return; }

            const event_id = ev.id;

            const { data: publicUserId, error: rpcErr } = await supabase
                .rpc("get_or_create_public_user", {
                    p_email: emailNorm,
                    p_first_name: values.firstName.trim(),
                    p_last_name: values.lastName.trim(),
                    p_source: "launch_landing",
                });

            if (rpcErr) throw rpcErr;



            // B) upsert canonical signup per (event_id, email)
            const { data: signup, error: signupErr } = await supabase
                .from("event_signups")
                .upsert([{
                    event_id: event_id,
                    email: emailNorm,
                    auth_user_id: authUser.id,     // for RLS ownership of this submission
                    public_user_id: publicUserId,
                    source: "launch_landing",
                    status: "new",
                }], { onConflict: "event_id,email" })
                .select("id")
                .single();

            if (signupErr) throw signupErr;

            // C) upsert details row (1:1 with signup)
            const { error: preregErr } = await supabase
                .from("launch_preregistrations")
                .insert([{
                    signup_id: signup.id,

                    first_name: values.firstName.trim(),
                    last_name: values.lastName.trim(),
                    email: emailNorm,

                    phone_country_iso2,
                    phone_calling_code,
                    phone_national,
                    phone_e164,

                    country: country_name,
                    residence_country_iso2: values.country,

                    privacy_consent: values.consent,
                    marketing_opt_in: values.marketingOptIn,

                    source: "launch_landing",
                    status: "new",
                }]);

            if (preregErr) {
                // if already exists, treat as success (or show “already registered”)
                if (preregErr.code === "23505") {
                    setSuccess(true);
                    setError("already registered");
                    return;
                }
                throw preregErr;
            }


            // Send confirmation email (don't block the user experience)
            try {
                await sendConfirmationEmail({
                    email: values.email.trim().toLowerCase(),
                    first_name: values.firstName.trim(),
                    my_message: myMessage,
                });
            } catch (emailError) {
                console.error("Error sending confirmation email:", emailError);
                // Optional: if you want to show something, reuse your existing `error` UI:
                // setError("Pre-registration received, but confirmation email could not be sent.");
            }

            try {
                await upsertKlaviyoApplicant({
                    email: values.email.trim().toLowerCase(),
                    first_name: values.firstName.trim(),
                    last_name: values.lastName.trim(),
                    phone_e164,
                    phone_country_iso2,
                    residence_country_iso2,
                    country_label: country_name,
                    privacy_consent: values.consent,
                    marketing_opt_in: values.marketingOptIn,
                    source: "launch_landing",
                    status: "new",

                });
            } catch (klaviyoErr) {
                console.error("Klaviyo sync failed:", klaviyoErr);
                // recommended: do not block user success
            }



            setSuccess(true);
            form.reset({
                firstName: "",
                lastName: "",
                email: "",
                phone_e164: "",
                country: "",
                consent: false,
                marketingOptIn: false,
            });
        } catch (e) {
            console.error("Supabase insert error:", e);

            const msg = String(e?.message ?? "").toLowerCase();
            const isEmailDuplicate =
                e?.code === "23505" && msg.includes("event_signups_unique_per_event_email");

            if (isEmailDuplicate) {
                setSuccess(true);
                setError("Already registered with this email.");
                return;
            }


            setError("Something went wrong while submitting. Please try again.");
        } finally {
            setSubmitting(false);
        }

    }

    return (
        <>
            <div className="relative mt-2 mx-2">
                <img
                    src={heroImg}
                    alt="Palais d’Antoia — Launch"
                    className="w-full h-auto object-cover aspect-[5/4] md:aspect-[16/5] rounded-3xl object-[50%_25%]"
                />
            </div>

            <div className="text-center max-w-prose mx-auto my-16 md:my-36">
                <div className="items-center space-x-3 mx-auto font-sans max-w-prose">
                    <Badge
                        variant="outline"
                        className=" bg-white/20 text-sm md:text-md border-2 px-4 md:px-6 font-medium py-2">
                        Invite-only </Badge>
                    <Badge variant="outline"
                        className="bg-white/20 text-sm md:text-md border-2 px-4 md:px-6 font-medium py-2">
                        Limited pre-registration </Badge>
                </div>

                <h1 className="text-5xl font-kalice font-medium m-8 px-6">
                    Palais d’Antoia: <br></br>Official Launch & Private view
                </h1>

                <p className="max-w-3xl text-black font-sans text-sm mx-auto px-6">
                    Be part of the beginning. After six years of meaningful interaction, Antoia emerges from Cypherloom in an intimate
                    evening of music, moving image, and a new artistic direction. Taking place.. at an undisclosed venue and time, which you will receive upon invitation.
                    Though this event is private, a limited number of seats are allocated to pre-registrations.
                </p>

                <div className="flex justify-between my-8 px-2 sm:px-6">
                    <Button
                        onClick={scrollToForm}
                        className="w-full mr-2 dark bg-black hover:bg-[#2c2c2c] rounded-none"
                        variant="secondary"
                    >
                        Pre-register
                    </Button>
                </div>
            </div>

            <Separator className='border border-white/20'></Separator>

            <div className='text-center max-w-prose mx-auto my-16 md:my-36 px-6'>
                <h2 className="text-3xl font-kalice font-medium m-4">About this event </h2>
                <p className="max-w-3xl text-black font-sans text-sm mx-auto">
                    We welcome you to celebrate with us our journey,
                    including collaborations with the Amsterdam Museum, Van Gogh Museum, AMFI,
                    Fashion for Good and Felix Meritis – and exclusive launch of our first collection
                    "Getting to Know You". A blend of Dutch heritage, data-responsive fashion and
                    human stories come together – designed by Gianni Antonia and Suza Vos.
                </p>

                {/* Details block */}
                <div className="my-24 font-sans font-medium">
                    <div className="rounded-2xl p-5 bg-white/40">
                        <h3 className="text-base font-medium mb-3">Event details*</h3>
                        <Separator className='border border-background my-4'></Separator>
                        <ul className="space-y-2 text-sm text-black">
                            <li className="flex justify-between gap-6">
                                <span className="text-black/60">Date</span>
                                <span>June, 2026</span>
                            </li>
                            <li className="flex justify-between gap-6">
                                <span className="text-black/60">Location</span>

                                <span className="flex items-center gap-2 text-black">
                                    <LockKeyhole className="h-4 w-4" />
                                    <span className="text-sm">Undisclosed</span>
                                </span>

                            </li>
                            <li className="flex justify-between gap-6">
                                <span className="text-black/60">Program</span>
                                <span>Music · Moving image · Private view</span>
                            </li>
                            <li className="flex justify-between gap-6">
                                <span className="text-black/60">Capacity</span>

                                <span className="flex items-center gap-2 text-black">
                                    <LockKeyhole className="h-4 w-4" />
                                    <span className="text-sm">Invite-only</span>
                                </span>


                            </li>
                        </ul>
                    </div>
                    <p className="text-black/80 text-sm my-4">*exact details will be disclosed upon invitation</p>
                </div>

            </div>

            <Separator className="border border-white/20" />

            <section
                id="register"
                ref={registerRef}
                className="max-w-prose font-sans text-sm items-center mx-auto px-6 sm:px-8 py-14 sm:py-20"
            >
                <div className="mb-16">
                    <h2 className="text-3xl font-kalice text-center font-medium p-6">
                        Pre-register
                    </h2>
                    <p className="text-black text-sm font-sans text-center">
                        Submit your details below. If selected, you’ll receive a personal
                        invitation with date, location, and confirmation steps. Seats are limited.
                    </p>
                </div>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>First name</FormLabel>
                                        <FormControl>
                                            <Input className="border-black border-2" placeholder="Your first name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Last name</FormLabel>
                                        <FormControl>
                                            <Input className="border-black border-2" placeholder="Your last name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Email</FormLabel>
                                    <FormControl>
                                        <Input className="border-black border-2" type="email" placeholder="you@domain.com" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* ✅ shadcn phone input */}
                        <FormField
                            control={form.control}
                            name="phone_e164"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Phone</FormLabel>
                                    <FormControl>
                                        <PhoneInput
                                            defaultCountry="NL"
                                            international
                                            placeholder="Enter phone number"
                                            value={field.value}
                                            onChange={(v) => field.onChange(v ?? "")}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Residence country (keep native for now; can swap to shadcn Select later) */}
                        <FormField
                            control={form.control}
                            name="country"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Country</FormLabel>
                                    <FormControl>
                                        <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                        >
                                            <SelectTrigger
                                                className="w-full border-black border-2 h-10 rounded-md px-3 text-black bg-transparent my-2"
                                            >
                                                <SelectValue placeholder="Select your country" />
                                            </SelectTrigger>

                                            <SelectContent>
                                                {RESIDENCE_COUNTRIES.map((c) => (
                                                    <SelectItem key={c.iso2} value={c.iso2}>
                                                        {c.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>

                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />


                        {/* Consent */}
                        <div className="my-6">
                            <FormField
                                control={form.control}
                                name="consent"
                                render={({ field }) => (
                                    <FormItem className="flex items-start gap-3">
                                        <FormControl>
                                            <CustomCheckbox
                                                checked={!!field.value}
                                                onChange={(v) => field.onChange(normalizeCheckboxValue(v))}
                                            />
                                        </FormControl>
                                        <div>
                                            <Label htmlFor="consent" className="font-normal font-sans text-sm">
                                                I agree that my details are used to manage my invitation and related
                                                communications, per the{" "}
                                                <Link
                                                    to="/privacystatement"
                                                    target="_blank"
                                                    className="font-sans_bold text-black underline hover:text-black/80"
                                                >
                                                    privacy statement
                                                </Link>
                                                .
                                            </Label>
                                            <FormMessage />
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="marketingOptIn"
                                render={({ field }) => (
                                    <FormItem className="flex items-start gap-3">
                                        <FormControl>
                                            <CustomCheckbox
                                                checked={!!field.value}
                                                onChange={(v) => field.onChange(normalizeCheckboxValue(v))}
                                            />
                                        </FormControl>
                                        <Label htmlFor="marketingOptIn" className="font-sans text-sm">
                                            Send me occasional Antoia updates (you can unsubscribe anytime).
                                        </Label>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="flex items-center gap-3" aria-live="polite">
                            <Button
                                type="submit"
                                disabled={submitting}
                                className={cn("rounded-none px-6", submitting && "opacity-70")}
                            >
                                {submitting ? "Submitting…" : "Pre-register"}
                            </Button>

                            {success && <span className="text-sm text-green-700">Thank you — received.</span>}
                            {error && <span className="text-sm text-red-700">{error}</span>}
                        </div>
                    </form>
                </Form>

                <p className="text-xs text-neutral-500 mt-6">
                    Invitations are limited. Pre-registration does not guarantee a seat; selections are handpicked to preserve the
                    intimacy of the evening.
                </p>
            </section>
        </>
    );
}
